import i18next from 'i18next';
import { isEmpty } from 'lodash';
import {
  formatPhoneNumberIntl,
  getCountries,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
import getCountryISO3 from 'country-iso-2-to-3';

export const formatLocalNumber = (localNumber) =>
  `${localNumber.substring(0, 3)}-${localNumber.substring(3)}`;

// react-phone-number-input has no iso3 -> iso2 lookup, so reverse the
// getCountryISO3 conversion over its supported country list. Used to tell
// the country picker which flag to show initially, since the flag can't be
// reliably re-derived from the number's digits alone for NANP countries.
// react-phone-number-input's getCountries() returns UPPERCASE iso2 codes
// (e.g. "US"), but react-intl-tel-input's own country data — and the
// `defaultCountry` prop it's matched against — is lowercase ("us"). Passing
// the uppercase code through silently fails the match (case-sensitive
// ===), so the picker falls back to whatever country happens to be first
// in its list instead of the one requested.
export const getIso2FromIso3CountryCode = (iso3CountryCode) => {
  if (!iso3CountryCode) {
    return undefined;
  }

  const iso2 = getCountries().find(
    (code) => getCountryISO3(code) === iso3CountryCode,
  );

  return iso2 && iso2.toLowerCase();
};

export const formatPhoneNumber = (
  phoneNumber,
  countries,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (!isEmpty(phoneNumber)) {
    const { Iso3CountryCode, AreaCode, LocalNumber } = phoneNumber;
    const country = countries?.find(
      (item) => item.Iso3Code === Iso3CountryCode,
    );

    if (
      shouldIgnoreAreaCodeHelperViaIso3(
        Iso3CountryCode,
        selectedISOCodesListToIgnoreAreaCode,
      )
    ) {
      const telephoneCode = `${country?.TelephoneCode}`.substring(0, 1);
      const areaCode = `${country?.TelephoneCode}`.substring(1, 4);

      return `+${telephoneCode || '#'} (${areaCode || '#'}) ${formatLocalNumber(
        LocalNumber,
      )}`;
    }

    return `+${country?.TelephoneCode || '#'} (${
      AreaCode || '#'
    }) ${formatLocalNumber(LocalNumber)}`;
  }

  return '';
};

export const isPhoneSelected = (values, phoneNumber) => {
  if (!isEmpty(values) && values?.PhoneNumber) {
    const parsedPhoneNumberValue = JSON.parse(values.PhoneNumber);
    const parsedPhoneNumber = JSON.parse(phoneNumber);

    if (
      parsedPhoneNumberValue.PhoneNumberUid === parsedPhoneNumber.PhoneNumberUid
    ) {
      return true;
    }
  }

  return false;
};

export const selectedPhoneNumberValidatorAndFormatter = (
  phoneNumber,
  setFieldError,
  countries,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (isEmpty(phoneNumber)) {
    return false;
  }

  const {
    Iso3CountryCode,
    AreaCode,
    LocalNumber,
    PhoneNumberUid,
  } = phoneNumber;
  const country = countries?.find((item) => item.Iso3Code === Iso3CountryCode);
  const number = `+${country?.TelephoneCode}${AreaCode || ''}${LocalNumber}`;

  if (!isPossiblePhoneNumber(number) || !isValidPhoneNumber(number)) {
    setFieldError(
      'PhoneNumber',
      i18next.t('validationSchema.general.phoneNumberNotValid'),
    );
    return;
  }

  return {
    Iso3CountryCode,
    AreaCode: shouldIgnoreAreaCodeHelperViaIso3(
      Iso3CountryCode,
      selectedISOCodesListToIgnoreAreaCode,
    )
      ? ''
      : AreaCode,
    LocalNumber,
    PhoneNumberUid,
  };
};

export const enteredPhoneNumberValidatorAndFormatter = (
  phoneNumber,
  setFieldError,
  selectedISOCodesListToIgnoreAreaCode,
  selectedIso2Country,
) => {
  const parsedNumber = parsePhoneNumber(`+${phoneNumber}`);
  const formatPhoneNumber = formatPhoneNumberIntl(`+${phoneNumber}`);
  const splitPhoneNumber = formatPhoneNumber.split(' ');

  if (!parsedNumber.country) {
    setFieldError(
      'PhoneNumber',
      i18next.t('validationSchema.general.phoneNumberNotValid'),
    );
    return;
  }

  // For NANP countries sharing the +1 dial code (US/CA/PR/...), re-parsing
  // the digits alone can silently resolve to a different country than the
  // one the user actually picked in the country dropdown (e.g. keeping US
  // digits after switching the flag to Canada). Trust the explicitly
  // selected country over libphonenumber's guess when it's available.
  const iso3CountryCode = selectedIso2Country
    ? getCountryISO3(selectedIso2Country.toUpperCase())
    : parsedNumber && getCountryISO3(parsedNumber.country);
  const areaCode = splitPhoneNumber[1];
  const localNumber = splitPhoneNumber
    .splice(2, splitPhoneNumber.length)
    .join()
    .replaceAll(',', '');

  return {
    Iso3CountryCode: iso3CountryCode,
    AreaCode: shouldIgnoreAreaCodeHelperViaIso3(
      iso3CountryCode,
      selectedISOCodesListToIgnoreAreaCode,
    )
      ? ''
      : areaCode,
    LocalNumber: localNumber,
  };
};

export const phoneNumberPostObject = (phoneNumber) => {
  if (phoneNumber) {
    const { Iso3CountryCode, AreaCode, LocalNumber } = phoneNumber;

    return {
      Iso3CountryCode,
      AreaCode,
      LocalNumber,
    };
  }

  return {};
};

export const shouldIgnoreAreaCodeHelper = (country, countries) => {
  const { iso2 } = country || {};

  if (!iso2 || !countries) {
    return false;
  }

  const iso3 = getCountryISO3(iso2.toUpperCase());

  if (iso3 && countries.includes(iso3)) {
    return true;
  }

  return false;
};

export const shouldIgnoreAreaCodeHelperViaIso3 = (iso3, countries) => {
  if (!iso3 || !countries) {
    return false;
  }

  if (countries.includes(iso3)) {
    return true;
  }

  return false;
};

export const phoneNumberToObjectHelper = (
  phoneNumber,
  setFieldError,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (!phoneNumber) {
    return {
      Iso3CountryCode: null,
      AreaCode: null,
      LocalNumber: null,
      StateCode: null,
    };
  }
  const parsedNumber = parsePhoneNumber(phoneNumber);
  const formatPhoneNumber = formatPhoneNumberIntl(phoneNumber);
  const splitPhoneNumber = formatPhoneNumber.split(' ');
  if (!parsedNumber.country) {
    setFieldError(
      'PhoneNumber',
      i18next.t('validationSchema.general.phoneNumberNotValid'),
    );
  }

  const iso3CountryCode = parsedNumber && getCountryISO3(parsedNumber.country);
  const countryCode = parsedNumber.countryCallingCode;
  const areaCode = splitPhoneNumber[1];
  const localNumber = splitPhoneNumber
    .splice(2, splitPhoneNumber.length)
    .join()
    .replaceAll(',', '');

  return {
    Iso3CountryCode: iso3CountryCode,
    AreaCode: shouldIgnoreAreaCodeHelperViaIso3(
      iso3CountryCode,
      selectedISOCodesListToIgnoreAreaCode,
    )
      ? ''
      : areaCode,
    LocalNumber: localNumber,
    StateCode: countryCode,
  };
};

export const validatePhoneNumber = (fieldValue) => {
  if (!(isPossiblePhoneNumber(fieldValue) && isValidPhoneNumber(fieldValue))) {
    return true;
  }

  return false;
};

export const formatPhoneNumberAsString = (
  phoneNumber,
  countries,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (!isEmpty(phoneNumber)) {
    const { Iso3CountryCode, AreaCode, LocalNumber } = phoneNumber;
    const country = countries?.find(
      (item) => item.Iso3Code === Iso3CountryCode,
    );

    if (
      shouldIgnoreAreaCodeHelperViaIso3(
        Iso3CountryCode,
        selectedISOCodesListToIgnoreAreaCode,
      )
    ) {
      const telephoneCode = `${country?.TelephoneCode}`.substring(0, 1);
      const areaCode = `${country?.TelephoneCode}`.substring(1, 4);
      return `+${telephoneCode}${areaCode}${LocalNumber}`;
    }
    return `+${country?.TelephoneCode}${AreaCode}${LocalNumber}`;
  }

  return '';
};

// react-intl-tel-input only honors `defaultCountry` for the initial flag
// when the value it's given has no leading dial code — once a "+<code>" is
// present it re-derives the flag purely from the digits, which for shared
// dial codes (NANP: US/CA/PR/...) can silently pick the wrong country and
// leaves `defaultCountry` unused. Pass this national-only number as the
// field's initial value (paired with `defaultCountry`) instead of
// formatPhoneNumberAsString's full "+<code>..." string.
export const formatPhoneNumberNationalAsString = (
  phoneNumber,
  countries,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (!isEmpty(phoneNumber)) {
    const { Iso3CountryCode, AreaCode, LocalNumber } = phoneNumber;

    if (
      shouldIgnoreAreaCodeHelperViaIso3(
        Iso3CountryCode,
        selectedISOCodesListToIgnoreAreaCode,
      )
    ) {
      const country = countries?.find(
        (item) => item.Iso3Code === Iso3CountryCode,
      );

      // countries hasn't finished loading yet (async fetch) — nothing
      // reliable to render, rather than stringifying "undefined" into the
      // area code.
      if (!country) {
        return '';
      }

      const areaCode = `${country.TelephoneCode}`.substring(1, 4);
      return `${areaCode}${LocalNumber}`;
    }
    return `${AreaCode}${LocalNumber}`;
  }

  return '';
};

export const formatPhoneNumberForView = (
  phoneNumber,
  countries,
  selectedISOCodesListToIgnoreAreaCode,
) => {
  if (!isEmpty(phoneNumber)) {
    const { Iso3CountryCode, AreaCode, LocalNumber } = phoneNumber;
    const country = countries?.find(
      (item) => item.Iso3Code === Iso3CountryCode,
    );

    if (
      shouldIgnoreAreaCodeHelperViaIso3(
        Iso3CountryCode,
        selectedISOCodesListToIgnoreAreaCode,
      )
    ) {
      const telephoneCode = `${country?.TelephoneCode}`.substring(0, 1);
      const areaCode = `${country?.TelephoneCode}`.substring(1, 4);

      return `+${telephoneCode || '#'} (${areaCode || '#'}) ${formatLocalNumber(
        LocalNumber,
      )}`;
    }

    return `+${country?.TelephoneCode || '#'} (${
      AreaCode || '#'
    }) ${formatLocalNumber(LocalNumber)}`;
  }

  return '';
};
